import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWR from "swr";
import {
  Table, Modal, Form, Select, message,
  Popconfirm, Button, Space, Descriptions,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getAssessTypeDetail,
  getAssessTypeLogicList, createAssessTypeLogic, updateAssessTypeLogic, deleteAssessTypeLogic,
  getAssessTypeAvailableCombos,
} from "../api/domainApi";
import { AssessTypeLogicRes, AssessTypeAvailableComboRes } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import UseYnTag from "../components/common/UseYnTag";

const stageOptions = [
  { value: null, label: "전체 (null)" },
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
];

const AssessTypeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const typeId = Number(id);

  const [logicModalOpen, setLogicModalOpen] = useState(false);
  const [editingLogic, setEditingLogic] = useState<AssessTypeLogicRes | null>(null);
  const [logicForm] = Form.useForm();

  const { data: assessType } = useSWR(
    typeId ? `/assess-type/${typeId}` : null,
    () => getAssessTypeDetail(typeId)
  );

  const { data: logics, mutate: mutateLogic } = useSWR(
    typeId ? `/assess-type/${typeId}/logic` : null,
    () => getAssessTypeLogicList(typeId)
  );

  const { data: availableCombos } = useSWR(
    typeId ? `/assess-type/${typeId}/available-combos` : null,
    () => getAssessTypeAvailableCombos(typeId)
  );

  const openLogicCreate = () => { setEditingLogic(null); logicForm.resetFields(); setLogicModalOpen(true); };
  const openLogicEdit = (row: AssessTypeLogicRes) => {
    setEditingLogic(row);
    logicForm.setFieldsValue(row);
    setLogicModalOpen(true);
  };

  const handleLogicOk = async () => {
    const values = await logicForm.validateFields();
    try {
      if (editingLogic) {
        await updateAssessTypeLogic(typeId, editingLogic.id, values);
        message.success("수정되었습니다.");
      } else {
        await createAssessTypeLogic(typeId, values);
        message.success("추가되었습니다.");
      }
      setLogicModalOpen(false);
      mutateLogic();
    } catch (e: any) { message.error(e.message); }
  };

  const handleLogicDelete = async (logicId: number) => {
    try {
      await deleteAssessTypeLogic(typeId, logicId);
      message.success("삭제되었습니다.");
      mutateLogic();
    } catch (e: any) { message.error(e.message); }
  };

  const logicColumns: ColumnsType<AssessTypeLogicRes> = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "X축 (S/T)", width: 140, render: (_, r) => r.xAxisLabel },
    { title: "Y축 (A/E)", width: 140, render: (_, r) => r.yAxisLabel },
    { title: "Z축 (I/V)", width: 140, render: (_, r) => r.zAxisLabel },
    {
      title: "관리", width: 120,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openLogicEdit(row)}>수정</Button>
          <Popconfirm title="삭제?" onConfirm={() => handleLogicDelete(row.id)}>
            <Button size="small" danger>삭제</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const comboColumns: ColumnsType<AssessTypeAvailableComboRes> = [
    { title: "조합키", dataIndex: "comboKey", width: 130 },
    { title: "X축 라벨", dataIndex: "xAxisLabel", width: 140 },
    { title: "Y축 라벨", dataIndex: "yAxisLabel", width: 140 },
    { title: "Z축 라벨", dataIndex: "zAxisLabel", ellipsis: { showTitle: true } },
  ];

  return (
    <div>
      <PageHeader title={`성향유형 상세 — ${assessType?.typeName ?? `#${typeId}`}`} />
      <Button style={{ marginBottom: 16 }} onClick={() => navigate("/assess-types")}>← 목록</Button>

      {assessType && (
        <Descriptions bordered size="small" style={{ marginBottom: 24 }} column={3}>
          <Descriptions.Item label="코드">{assessType.typeCode}</Descriptions.Item>
          <Descriptions.Item label="유형명">{assessType.typeName}</Descriptions.Item>
          <Descriptions.Item label="수식어">{assessType.subName}</Descriptions.Item>
          <Descriptions.Item label="좌표">({assessType.coordX},{assessType.coordY},{assessType.coordZ})</Descriptions.Item>
          <Descriptions.Item label="기본">{assessType.isDefault ? "O" : "-"}</Descriptions.Item>
          <Descriptions.Item label="사용여부"><UseYnTag value={assessType.useYn} /></Descriptions.Item>
        </Descriptions>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <strong>로직 목록</strong>
        <Button type="primary" onClick={openLogicCreate}>로직 추가</Button>
      </div>
      <Table rowKey="id" columns={logicColumns} dataSource={logics} pagination={false} size="small" style={{ marginBottom: 32 }} />

      <div style={{ marginBottom: 12 }}>
        <strong>사용 가능 조합</strong>
      </div>
      <Table rowKey="comboKey" columns={comboColumns} dataSource={availableCombos} pagination={false} size="small" />

      <Modal
        title={editingLogic ? "로직 수정" : "로직 추가"}
        open={logicModalOpen} onOk={handleLogicOk} onCancel={() => setLogicModalOpen(false)}
        okText={editingLogic ? "수정" : "추가"} cancelText="취소"
      >
        <Form form={logicForm} layout="vertical" style={{ marginTop: 16 }}>
          <Space wrap>
            <Form.Item name="sStage" label="S단계"><Select style={{ width: 100 }} options={stageOptions} /></Form.Item>
            <Form.Item name="tStage" label="T단계"><Select style={{ width: 100 }} options={stageOptions} /></Form.Item>
          </Space>
          <Space wrap>
            <Form.Item name="aStage" label="A단계"><Select style={{ width: 100 }} options={stageOptions} /></Form.Item>
            <Form.Item name="eStage" label="E단계"><Select style={{ width: 100 }} options={stageOptions} /></Form.Item>
          </Space>
          <Space wrap>
            <Form.Item name="iStage" label="I단계"><Select style={{ width: 100 }} options={stageOptions} /></Form.Item>
            <Form.Item name="vStage" label="V단계"><Select style={{ width: 100 }} options={stageOptions} /></Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default AssessTypeDetailPage;
